import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl, toUploadUrl } from '../../config/api';
import '../../styles/admin-hero-slides.css';

const initialFormState = {
    title: '',
    subtitle: '',
    description: '',
    cta_text: '',
    cta_link: '/packages',
    sort_order: 1,
    status: 'active'
};

const HeroSlides = () => {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedSlide, setSelectedSlide] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Session expired. Please login again.');
            window.location.href = '/admin/login';
            return null;
        }

        return token;
    };

    const fetchSlides = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl('/hero-slides/admin/all'), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            const slidesData = response.data?.data || [];
            setSlides(slidesData);
        } catch (error) {
            console.error('Error fetching hero slides:', error);
            alert(error.response?.data?.message || 'Failed to fetch hero slides');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSlides();
    }, [fetchSlides]);

    const activeCount = useMemo(
        () => slides.filter((slide) => String(slide.status).toLowerCase() === 'active').length,
        [slides]
    );

    const resetForm = () => {
        setSelectedSlide(null);
        setFormData({
            ...initialFormState,
            sort_order: slides.length + 1
        });
        setSelectedFile(null);
        setPreviewUrl('');
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (slide) => {
        setSelectedSlide(slide);
        setFormData({
            title: slide.title || '',
            subtitle: slide.subtitle || '',
            description: slide.description || '',
            cta_text: slide.cta_text || '',
            cta_link: slide.cta_link || '/packages',
            sort_order: slide.sort_order || 0,
            status: slide.status || 'active'
        });
        setSelectedFile(null);
        setPreviewUrl(slide.image ? toUploadUrl(slide.image) : '');
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setSelectedFile(null);
            return;
        }

        if (!file.type.startsWith('image/')) {
            alert('Please select a valid image file');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('Image size should be less than 10MB');
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert('Slide title is required');
            return;
        }

        if (!selectedSlide && !selectedFile) {
            alert('Please upload a hero image');
            return;
        }

        const token = getToken();
        if (!token) return;

        try {
            setSaving(true);
            const payload = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                payload.append(key, value ?? '');
            });

            if (selectedFile) {
                payload.append('image', selectedFile);
            }

            const url = selectedSlide
                ? buildApiUrl(`/hero-slides/${selectedSlide.id}/update`)
                : buildApiUrl('/hero-slides');

            await axios.post(url, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert(`Hero slide ${selectedSlide ? 'updated' : 'created'} successfully`);
            setShowModal(false);
            resetForm();
            fetchSlides();
        } catch (error) {
            console.error('Error saving hero slide:', error);
            alert(error.response?.data?.message || 'Failed to save hero slide');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (slideId) => {
        if (!window.confirm('Are you sure you want to delete this hero slide?')) {
            return;
        }

        const token = getToken();
        if (!token) return;

        try {
            await axios.delete(buildApiUrl(`/hero-slides/${slideId}`), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            alert('Hero slide deleted successfully');
            fetchSlides();
        } catch (error) {
            console.error('Error deleting hero slide:', error);
            alert(error.response?.data?.message || 'Failed to delete hero slide');
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader />

                <div className="hero-slides-admin-page">
                    <div className="hero-slides-page-header">
                        <div>
                            <p className="hero-slides-kicker">Homepage Content</p>
                            <h1>Hero Slider Management</h1>
                            <p className="hero-slides-subtitle">
                                Home page ke top slider ko yahin se upload, reorder, activate, update aur delete kar sakte ho.
                            </p>
                        </div>
                        <button className="add-slide-btn" onClick={openCreateModal}>
                            <i className="fas fa-plus"></i> Add Hero Slide
                        </button>
                    </div>

                    <div className="hero-slides-stats-grid">
                        <div className="hero-slides-stat-card">
                            <span>Total Slides</span>
                            <strong>{slides.length}</strong>
                        </div>
                        <div className="hero-slides-stat-card">
                            <span>Active Slides</span>
                            <strong>{activeCount}</strong>
                        </div>
                        <div className="hero-slides-stat-card">
                            <span>Inactive Slides</span>
                            <strong>{slides.length - activeCount}</strong>
                        </div>
                    </div>

                    <div className="hero-slides-table-card">
                        {loading ? (
                            <div className="hero-slides-empty">Loading hero slides...</div>
                        ) : slides.length === 0 ? (
                            <div className="hero-slides-empty">No hero slides added yet.</div>
                        ) : (
                            <div className="hero-slides-grid">
                                {slides.map((slide) => (
                                    <div className="hero-slide-admin-card" key={slide.id}>
                                        <div className="hero-slide-preview">
                                            <img
                                                src={toUploadUrl(slide.image, { fallback: 'https://placehold.co/600x320?text=Hero+Slide' })}
                                                alt={slide.title}
                                            />
                                            <span className={`hero-slide-status ${slide.status}`}>
                                                {slide.status}
                                            </span>
                                        </div>

                                        <div className="hero-slide-admin-content">
                                            <div className="hero-slide-meta-row">
                                                <span>Order: {slide.sort_order}</span>
                                                <span>{slide.cta_text || 'No CTA text'}</span>
                                            </div>
                                            <h3>{slide.title}</h3>
                                            <p>{slide.subtitle || 'No subtitle added'}</p>
                                            <small>{slide.cta_link || 'No link set'}</small>
                                        </div>

                                        <div className="hero-slide-admin-actions">
                                            <button onClick={() => openEditModal(slide)} className="edit-btn">
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            <button onClick={() => handleDelete(slide.id)} className="delete-btn">
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="hero-slide-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{selectedSlide ? 'Edit Hero Slide' : 'Add Hero Slide'}</h2>
                                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                            </div>

                            <form onSubmit={handleSubmit} className="hero-slide-form">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Subtitle</label>
                                        <input
                                            type="text"
                                            name="subtitle"
                                            value={formData.subtitle}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Description</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>CTA Text</label>
                                        <input
                                            type="text"
                                            name="cta_text"
                                            value={formData.cta_text}
                                            onChange={handleInputChange}
                                            placeholder="Explore Packages"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>CTA Link</label>
                                        <input
                                            type="text"
                                            name="cta_link"
                                            value={formData.cta_link}
                                            onChange={handleInputChange}
                                            placeholder="/packages"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Sort Order</label>
                                        <input
                                            type="number"
                                            min="0"
                                            name="sort_order"
                                            value={formData.sort_order}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Hero Image {!selectedSlide && '*'}</label>
                                        <input type="file" accept="image/*" onChange={handleFileChange} />
                                    </div>
                                </div>

                                {previewUrl && (
                                    <div className="hero-slide-form-preview">
                                        <img src={previewUrl} alt="Hero preview" />
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="save-btn" disabled={saving}>
                                        {saving ? 'Saving...' : selectedSlide ? 'Update Slide' : 'Create Slide'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeroSlides;
